// src/pages/Dashboard/index.jsx
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import DashboardLayout from "../../components/DashboardLayout";
import API from "../../utils/api";
import styles from "./Dashboard.module.css";
import RenderToast from "../../components/RenderToast";
import LoaderComp from "../../components/LoaderComp";
import { FiBox, FiFolder, FiTag, FiPlus, FiUsers, FiMail } from "react-icons/fi";

const readUser = () => {
  try {
    const raw = localStorage.getItem("user");
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

export default function Dashboard() {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    products: 0,
    categories: 0,
    brands: 0,
  });
  const [user, setUser] = useState(readUser());

  useEffect(() => {
    const handleStorage = () => setUser(readUser());
    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, []);

  const can = (permission) => !!(user && Number(user[permission]) === 1);
  const showQuickActions = can("dashboard_quick_action");
  const hideViewLinks = can("dashboard_hide_view_all");
  const removeStatsCards = can("dashboard_remove_cards");
  const showProductCard = can("dashboard_card_products");
  const showCategoryCard = can("dashboard_card_categories");
  const showBrandCard = can("dashboard_card_brands");
  const showSummaryCards =
    !removeStatsCards &&
    (showProductCard || showCategoryCard || showBrandCard);

  useEffect(() => {
    fetchDashboardStats();
  }, []);

  const fetchDashboardStats = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("authToken");

      const response = await fetch(`${API.base}/dashboard_counts.php`, {
        method: "POST",
        headers: {
          Authorization: token,
          "Content-Type": "application/json",
        },
      });

      if (response.ok) {
        const data = await response.json();
        setStats(data);
      } else {
        RenderToast({
          type: "error",
          message: "Failed to load dashboard statistics",
        });
      }
    } catch (err) {
      console.error("Dashboard error:", err);
      RenderToast({
        type: "error",
        message: "Network error. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <LoaderComp />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className={styles.dashboardContainer}>
        <div className={styles.dashboardHeader}>
          <h2 className={styles.title}>Dashboard</h2>
          <p className={styles.subtitle}>Welcome to your admin panel</p>
        </div>

        {showSummaryCards && (
          <div className={styles.statsGrid}>
            {showProductCard && (
              <div className={styles.statCard}>
                <div className={styles.statIcon}>
                  <FiBox />
                </div>
                <h3 className={styles.statNumber}>{stats.products}</h3>
                <h6 className={styles.statLabel}>Total Products</h6>
                {!hideViewLinks && (
                  <Link to="/product/view" className={styles.viewLink}>
                    View All →
                  </Link>
                )}
              </div>
            )}

            {showCategoryCard && (
              <div className={styles.statCard}>
                <div className={styles.statIcon}>
                  <FiFolder />
                </div>
                <h3 className={styles.statNumber}>{stats.categories}</h3>
                <h6 className={styles.statLabel}>Total Categories</h6>
                {!hideViewLinks && (
                  <Link to="/category/view" className={styles.viewLink}>
                    View All →
                  </Link>
                )}
              </div>
            )}

            {showBrandCard && (
              <div className={styles.statCard}>
                <div className={styles.statIcon}>
                  <FiTag />
                </div>
                <h3 className={styles.statNumber}>{stats.brands}</h3>
                <h6 className={styles.statLabel}>Total Brands</h6>
                {!hideViewLinks && (
                  <Link to="/brand/view" className={styles.viewLink}>
                    View All →
                  </Link>
                )}
              </div>
            )}
          </div>
        )}

        {showQuickActions && (
          <div className={styles.quickActionsWrapper}>
            <div className={styles.quickActionCard}>
              <h5 className={styles.actionTitle}>Quick Actions</h5>
              <div className={styles.quickActions}>
                {can("product_add") && (
                  <Link to="/product/add" className={styles.actionLink}>
                    <FiPlus /> Add Product
                  </Link>
                )}
                {can("category_add") && (
                  <Link to="/category/add" className={styles.actionLink}>
                    <FiPlus /> Add Category
                  </Link>
                )}
                {can("brand_add") && (
                  <Link to="/brand/add" className={styles.actionLink}>
                    <FiPlus /> Add Brand
                  </Link>
                )}
                {can("user_manage") && (
                  <Link to="/users" className={styles.actionLink}>
                    <FiUsers /> Manage Users
                  </Link>
                )}
                {can("contact_access") && (
                  <Link to="/contact/messages" className={styles.actionLink}>
                    <FiMail /> Contact Inbox
                  </Link>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
